import { css } from '@flex-design-system/stitches-react';
import { useResizeObserver } from '@flex-packages/hooks-common';
import {
  $generateHtmlFromNodes,
  createHeadlessEditor,
  getNodeListByFeatures,
  VIEW_MODE_THEME,
} from '@flex-services/flexical';
import { useMemo, useState } from 'react';

interface Props {
  initialState: string;
}

export function FlexicalRenderer(props: Props) {
  const { initialState } = props;

  /**
   * 왜 headlessEditor를 사용하나요?
   *
   * - 이런 문제가 있었습니다. https://flex-cv82520.slack.com/archives/CS05YNN9H/p1752548472860039
   * - 그런데 이 문제는 Next.js 어플리케이션인 careers team 앱에서만 발생했습니다. 이 문제는 위 스레드에 적혀있듯 esm을 지원하는 버전부터 문제가 발생하고 있는데, next.js의 esm 호환성 문제는 전부터 여러번 불거졌습니다.
   *   - next.js의 ESM,CJS 호환성 사례 예시
   *     1. https://github.com/flex-team/flex-frontend-services/pull/356
   *     2. https://linear.app/flexteam/issue/WCP-1126
   *     3. https://flex-cv82520.slack.com/archives/CS05YNN9H/p1668393064302629?thread_ts=1668393062.044269&cid=CS05YNN9H
   *     4. https://flex-cv82520.slack.com/archives/C05RWJ3AMGE/p1734496026564109
   *     5. https://flex-cv82520.slack.com/archives/C02CWLDRP6F/p1705578425612119
   * - 다행히 careers team에서는 readonly 모드만 사용하고 있어서 headless editor로 동작 우회가 가능합니다.
   *
   * (그런데 headless editor 쓰면 SSR 지원도 되어서 오히려 좋은 것일수도)
   */
  const html = useMemo(() => {
    const editor = createHeadlessEditor({
      theme: VIEW_MODE_THEME,
      editable: false,
      nodes: getNodeListByFeatures({
        editable: false,
        features: ['Heading', 'Image'],
      }),
    });

    editor.setEditorState(editor.parseEditorState(initialState));

    return editor.read(() => {
      return $generateHtmlFromNodes(editor);
    });
  }, [initialState]);

  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const { width: elementWidth } = useElementContentRectSize(element);

  /**
   *
   * flexical의 ImageNode가 런타임에 조정해주던 maxHeight 조정 로직을 이곳에도 반영합니다.
   * @TODO ImageNode의 exportDOM 기능에 이것을 탑재할 수는 없을까?
   *
   * # 관련해서 발생했던 이슈
   * https://flex-cv82520.slack.com/archives/CRU35U9FC/p1752646195504669
   */
  const imageResizedHtml = useMemo(() => {
    const dom = new DOMParser().parseFromString(html, 'text/html');
    const images = dom.querySelectorAll('img');

    images.forEach(image => {
      const width = parseInt(image.getAttribute('width') ?? '', 10);
      const height = parseInt(image.getAttribute('height') ?? '', 10);

      image.style.maxWidth = '100%';

      // height 값이 없으면 알아서 너비에 맞게 이미지의 비율이 조정됨
      if (Number.isNaN(height)) {
        return;
      }

      // height 값 계산이 불가하면 auto로 설정
      if (Number.isNaN(width) || elementWidth === null) {
        image.style.height = 'auto';
        return;
      }

      image.style.maxHeight = (height / width) * elementWidth + 'px';
    });

    return dom.body.innerHTML;
  }, [html, elementWidth]);

  return (
    <div
      ref={setElement}
      // headless editor에서 생성된 html은 root를 포함하지 않고 있음.
      // https://flex-cv82520.slack.com/archives/CRU35U9FC/p1752802094400669
      className={`${VIEW_MODE_THEME.root ?? ''} ${editorStyles()}`}
      style={{
        fontSize: '16px',
      }}
      /**
       * LexicalNode에 선언된 exportDOM 메서드로 만들어지는 html 컨텐츠라 XSS free가 보장됨
       */
      dangerouslySetInnerHTML={{ __html: imageResizedHtml }}
    />
  );
}

const editorStyles = css({
  '& li:not(:first-child)': {
    marginTop: 8,
  },
});

const useElementContentRectSize = (element: HTMLDivElement | null) => {
  const [elementWidth, setElementWidth] = useState<number | null>(null);
  const [elementHeight, setElementHeight] = useState<number | null>(null);

  useResizeObserver(element, () => {
    if (element !== null) {
      setElementWidth(element.clientWidth);
      setElementHeight(element.clientHeight);
    }
  });

  return {
    width: elementWidth,
    height: elementHeight,
  };
};
